/**
 * Construction Manager
 * 
 * Handles building construction with queues, timers, and cost validation.
 */

import { getBuildingCost, getBuildingKeepRequirement } from '../core/BuildingData.js';
import { GameEngine } from '../core/GameEngine.js';

export class ConstructionManager {
    constructor(gameState, gameEngine) {
        this.gameState = gameState;
        this.gameEngine = gameEngine;
        this.queue = [];
        this.nextQueueId = 1;
    }
    
    /**
     * Add a building to the construction queue
     * @param {number} buildingType - Building constant (e.g., CUTTER, FARM)
     * @param {number} level - Building level (1-10)
     * @param {number} x - X position (1-20)
     * @param {number} y - Y position (1-20)
     * @returns {Object|null} Queue item or null if failed
     */
    queueBuilding(buildingType, level, x, y) {
        // Validate position
        if (!this.isValidPosition(x, y)) {
            console.error(`Invalid position: [${x}, ${y}]`);
            return null;
        }
        
        // Check if position is already occupied
        if (this.gameEngine.map[x][y] !== 0) {
            console.error(`Position [${x}, ${y}] is already occupied`);
            return null;
        }
        
        // Get building cost
        const cost = getBuildingCost(buildingType, level);
        if (!cost || cost.buildTime === 0) {
            console.error(`Invalid building type or level: ${buildingType}, ${level}`);
            return null;
        }
        
        // Check Keep level requirement
        const keepLevel = this.gameEngine.basilicaLevel || 10;
        const requiredKeepLevel = getBuildingKeepRequirement(buildingType);
        if (keepLevel < requiredKeepLevel) {
            console.error(`Keep level ${keepLevel} is too low. Required: ${requiredKeepLevel}`);
            return null;
        }
        
        // Check if player has enough resources
        const resourceCost = {
            wood: cost.wood || 0,
            stone: cost.stone || 0,
            iron: 0, // Most buildings don't require iron
            food: 0,
            gold: 0
        };
        
        if (!this.gameState.hasResources(resourceCost)) {
            console.error('Insufficient resources');
            return null;
        }
        
        // Create queue item
        const queueItem = {
            id: this.nextQueueId++,
            buildingType,
            level,
            x,
            y,
            cost: resourceCost,
            buildTime: cost.buildTime, // in seconds
            status: 'queued',
            createdAt: Date.now(),
            startedAt: null,
            completesAt: null,
            timerId: null
        };
        
        // Add to queue
        this.queue.push(queueItem);
        
        // Try to start construction immediately if resources are available
        this.processQueue();
        
        return queueItem;
    }
    
    /**
     * Process the construction queue
     */
    processQueue() {
        // Sort queue by creation time (FIFO)
        this.queue.sort((a, b) => a.createdAt - b.createdAt);
        
        for (const item of this.queue) {
            if (item.status === 'queued') {
                // Check if we have resources
                if (this.gameState.hasResources(item.cost)) {
                    this.startConstruction(item);
                }
            }
        }
    }
    
    /**
     * Start construction of a queued building
     * @param {Object} queueItem - Queue item to start
     */
    startConstruction(queueItem) {
        // Spend resources
        if (!this.gameState.spendResources(queueItem.cost)) {
            return; // Failed to spend resources
        }
        
        // Update status
        queueItem.status = 'building';
        queueItem.startedAt = Date.now();
        queueItem.completesAt = Date.now() + (queueItem.buildTime * 1000);
        
        // Create timer
        queueItem.timerId = this.gameState.timerManager.createTimer(
            'construction',
            queueItem.buildTime,
            (timerId, data) => {
                this.completeConstruction(data.queueItemId);
            },
            { queueItemId: queueItem.id }
        );
        
        // Emit event
        this.gameState.emit('constructionStarted', queueItem);
    }
    
    /**
     * Complete a construction
     * @param {number} queueItemId - Queue item ID
     */
    completeConstruction(queueItemId) {
        const item = this.queue.find(q => q.id === queueItemId);
        if (!item || item.status !== 'building') {
            return;
        }
        
        // Place building on map
        this.gameEngine.map[item.x][item.y] = item.buildingType;
        
        // Update building level if needed
        if (this.gameEngine.buildingLevel !== item.level) {
            this.gameEngine.buildingLevel = item.level;
        }
        
        // Recalculate totals
        this.gameEngine.updateTotals();
        this.gameState.updateStorage();
        
        // Update status
        item.status = 'completed';
        
        // Remove from queue
        const index = this.queue.indexOf(item);
        if (index > -1) {
            this.queue.splice(index, 1);
        }
        
        // Emit event
        this.gameState.emit('buildingCompleted', {
            buildingType: item.buildingType,
            level: item.level,
            x: item.x,
            y: item.y
        });
        
        // Process next item in queue
        this.processQueue();
    }
    
    /**
     * Cancel a queued or building construction
     * @param {number} queueItemId - Queue item ID
     * @returns {boolean} True if cancelled successfully
     */
    cancelConstruction(queueItemId) {
        const item = this.queue.find(q => q.id === queueItemId);
        if (!item) {
            return false;
        }
        
        // Can only cancel queued or building items
        if (item.status === 'completed' || item.status === 'cancelled') {
            return false;
        }
        
        // Cancel timer if building
        if (item.timerId) {
            this.gameState.timerManager.cancelTimer(item.timerId);
        }
        
        // Refund resources if building was started
        if (item.status === 'building') {
            this.gameState.addResources(item.cost);
        }
        
        // Update status
        item.status = 'cancelled';
        
        // Remove from queue
        const index = this.queue.indexOf(item);
        if (index > -1) {
            this.queue.splice(index, 1);
        }
        
        // Emit event
        this.gameState.emit('constructionCancelled', item);
        
        // Process next item in queue
        this.processQueue();
        
        return true;
    }
    
    /**
     * Get construction queue
     * @returns {Array} Array of queue items
     */
    getQueue() {
        return this.queue.map(item => ({
            ...item,
            remainingTime: item.timerId ? 
                this.gameState.timerManager.getRemainingTime(item.timerId) : 
                (item.buildTime - ((Date.now() - item.startedAt) / 1000)),
            progress: item.timerId ?
                this.gameState.timerManager.getProgress(item.timerId) :
                0
        }));
    }
    
    /**
     * Get queue item by ID
     * @param {number} queueItemId - Queue item ID
     * @returns {Object|null} Queue item or null
     */
    getQueueItem(queueItemId) {
        return this.queue.find(q => q.id === queueItemId) || null;
    }
    
    /**
     * Check if position is valid
     * @param {number} x - X position
     * @param {number} y - Y position
     * @returns {boolean} True if valid
     */
    isValidPosition(x, y) {
        // Valid positions are 1-20 (0 and 20 are borders)
        return x >= 1 && x < 20 && y >= 1 && y < 20;
    }
    
    /**
     * Clear all queued constructions (not building ones)
     */
    clearQueued() {
        const queued = this.queue.filter(q => q.status === 'queued');
        for (const item of queued) {
            const index = this.queue.indexOf(item);
            if (index > -1) {
                this.queue.splice(index, 1);
            }
        }
    }
    
    /**
     * Get queue statistics
     * @returns {Object} Statistics
     */
    getStats() {
        return {
            total: this.queue.length,
            queued: this.queue.filter(q => q.status === 'queued').length,
            building: this.queue.filter(q => q.status === 'building').length,
            completed: this.queue.filter(q => q.status === 'completed').length
        };
    }
}

